<template>
  <button
    type="button"
    :class="[
      'inline-flex items-center justify-center rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-palette-amber/30',
      compact ? 'h-6 w-6' : 'h-7 w-7',
      starred
        ? 'bg-theme-palette-amber/10 text-theme-palette-amber hover:bg-theme-palette-amber/10 dark:bg-theme-palette-amber/30 dark:text-theme-palette-amber dark:hover:bg-theme-palette-amber/40'
        : 'bg-theme-bg-subtle/70 text-theme-text-secondary hover:bg-theme-palette-amber/10 hover:text-theme-palette-amber dark:bg-theme-surface-elevated dark:hover:bg-theme-palette-amber/30 dark:hover:text-theme-palette-amber',
    ]"
    :aria-pressed="starred"
    :aria-label="starred ? unfavoriteLabel : favoriteLabel"
    :title="starred ? unfavoriteLabel : favoriteLabel"
    @click.stop.prevent="toggle"
  >
    <Icon :path="starred ? mdiStar : mdiStarOutline" :size="size" />
  </button>
</template>

<script setup>
  /* eslint-disable no-undef */
  import Icon from '@/components/Icon.vue';
  import { useNotification } from '@/composables/useNotification';
  import { addFavorite, favoritesById, removeFavorite } from '@/composables/favorites';
  import { mdiStar, mdiStarOutline } from '@mdi/js';
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';

  const props = defineProps({
    id: { type: String, required: true },
    kind: { type: String, required: true },
    label: { type: String, required: true },
    to: { type: String, required: true },
    icon: { type: String, default: null },
    size: { type: [Number, String], default: 14 },
    compact: { type: Boolean, default: false },
  });

  const { t } = useI18n();
  const { notifySuccess, notifyError } = useNotification();

  const starred = computed(() => favoritesById.value.has(props.id));
  const favoriteLabel = computed(() => t('favorites.add'));
  const unfavoriteLabel = computed(() => t('favorites.remove'));

  async function toggle() {
    if (starred.value) {
      try {
        await removeFavorite(props.id);
        notifySuccess(t('messages.favorite_removed'));
      } catch {
        // optimistic state was reverted inside removeFavorite on failure
        notifyError(t('messages.favorite_update_failed'));
      }
      return;
    }
    try {
      await addFavorite({
        id: props.id,
        kind: props.kind,
        label: props.label,
        to: props.to,
        icon: props.icon,
      });
      notifySuccess(t('messages.favorite_added'));
    } catch {
      // optimistic state was reverted inside addFavorite on failure
      notifyError(t('messages.favorite_update_failed'));
    }
  }
</script>
